// useDictionaries.ts
import { ref, type Ref, computed } from 'vue'
import { getToken } from '@/utils/cookies'
import { getDictList } from '@/api/user'
import { getUnitList, getTermBank, getEnumData } from '@/api/common'

// ==================== 类型定义 ====================
type DictionaryItem = Record<string, any>
type DictionaryData = DictionaryItem[]

// 枚举数据项（CommonApi/Common/GetEnumData 返回结构）
interface EnumItem {
  key: number
  val: string
}

// 字典配置项
interface DictConfig {
  key: string // 字典在 dictionariesKV 中的 key
  type?: number // 字典类型（有此配置的走 isEnable 过滤，停用项不进启用列表）
  fetcher: () => Promise<DictionaryData> // 对应的数据获取函数
}

// ==================== 字典获取函数（保留原有逻辑） ====================
// 获取通用字典（通过 type），返回含停用项的原始列表，过滤在 loadDictionary 统一做
const fetchDictByType = async (type: number): Promise<DictionaryData> => {
  const res = await getDictList({ type }, { useCache: true })
  if ([1, 2].includes(type) && !res.some((item: any) => item.enTitle === 'Not selected')) {
    res.unshift({
      id: 0,
      type: type,
      title: localStorage.getItem('tcma-lang') === 'zh-CN' ? '未选择' : '未選擇',
      enTitle: 'Not selected',
      sort: 0,
      isEnable: true,
    })
  }
  return res
}

// 获取单位列表
const fetchUnitList = async (): Promise<DictionaryData> => {
  const res: Record<string, any> = await getUnitList()
  unitTcEnMap.value = {}
  res.forEach((v: Record<string, any>) => {
    unitTcEnMap.value[v.unit] = v.enUnit
  })
  return res as DictionaryData
}

// 获取字库列表
const fetchTermBankList = async (): Promise<DictionaryData> => {
  const res = await getTermBank()
  return res as DictionaryData
}

// ==================== 字典配置（声明所有需要懒加载的字典） ====================
const dictConfigs: DictConfig[] = [
  // 来自 DictionariesMap 的字典
  { key: 'acupunctureMethod', type: 1, fetcher: () => fetchDictByType(1) },
  { key: 'acupuncturePosition', type: 2, fetcher: () => fetchDictByType(2) },
  { key: 'medicantFunc', type: 3, fetcher: () => fetchDictByType(3) },
  { key: 'medicantBrand', type: 4, fetcher: () => fetchDictByType(4) },
  { key: 'ratioMedicantStandard', type: 5, fetcher: () => fetchDictByType(5) },
  { key: 'payMethod', type: 6, fetcher: () => fetchDictByType(6) },
  // 化验检查单类型（dict 表 Type=8）
  { key: 'labExamType', type: 8, fetcher: () => fetchDictByType(8) },
  // 单位列表单独配置
  { key: 'unitList', fetcher: fetchUnitList },
]

// ==================== 全局状态（单例） ====================
// 字典缓存（仅启用项，供下拉/筛选使用）
export const dictionariesKV = ref<Record<string, DictionaryData>>({})
// 字典原始缓存（含停用项，仅用于表單回显历史数据中已停用的字典值）
export const dictionariesRawKV = ref<Record<string, DictionaryData>>({})
// 字库列表缓存
export const termBankList = ref<DictionaryData>([])
// 字典类型列表缓存（枚举 type === 2 时存储）
export const dictTypeList = ref<EnumItem[]>([])
// 字库列表加载状态
const termBankListLoaded = ref(false)
const termBankListPending = ref<Promise<DictionaryData> | null>(null)

const unitTcEnMap = ref<Record<string, string>>({})

// 记录正在进行的请求，用于去重
const pendingRequests = new Map<string, Promise<DictionaryData>>()

// ==================== 核心懒加载函数 ====================
/**
 * 加载指定的字典（按需加载，带缓存和请求去重）
 * @param key 字典 key
 * @returns Promise<DictionaryData>
 */
export async function loadDictionary(key: string): Promise<DictionaryData> {
  // 1. 如果已有数据，直接返回
  if (dictionariesKV.value[key]) {
    return dictionariesKV.value[key]
  }

  // 2. 如果相同 key 的请求正在进行中，复用该 Promise
  if (pendingRequests.has(key)) {
    return pendingRequests.get(key)!
  }

  // 3. 查找对应的配置
  const config = dictConfigs.find((c) => c.key === key)
  if (!config) {
    throw new Error(`未找到字典配置: ${key}`)
  }

  // 4. 发起新请求，并存入 pendingRequests
  const promise = config
    .fetcher()
    .then((data) => {
      dictionariesRawKV.value[key] = data
      // 药品品牌（type 4）顺序完全由后台字典的拖动排序决定，前端不得重排（展示顺序 = 接口返回顺序）
      const enabledList = config.type ? data.filter((item: any) => item.isEnable) : data
      dictionariesKV.value[key] = enabledList
      pendingRequests.delete(key)
      return enabledList
    })
    .catch((err) => {
      pendingRequests.delete(key)
      throw err
    })
  pendingRequests.set(key, promise)

  return promise
}

/**
 * 加载字库列表（懒加载，全局只加载一次）
 */
export async function loadTermBank(): Promise<DictionaryData> {
  if (termBankListLoaded.value) {
    return termBankList.value
  }
  if (termBankListPending.value) {
    return termBankListPending.value
  }

  const promise = fetchTermBankList()
    .then((data) => {
      termBankList.value = data
      termBankListLoaded.value = true
      termBankListPending.value = null
      return data
    })
    .catch((err) => {
      termBankListPending.value = null
      throw err
    })
  termBankListPending.value = promise
  return promise
}

/**
 * 加载枚举数据（type: 1-国籍、2-字典类型、3-证件类型）
 * @param type 枚举类型，传入 2 时结果会存储到 dictTypeList
 * @returns Promise<EnumItem[]>
 */
export async function loadEnum(type: number, options?: { dictTypeMap?: Record<any, any> }): Promise<EnumItem[]> {
  const res = await getEnumData({ dataEnum: type }, { useCache: true })
  if (type === 2) {
    dictTypeList.value = res.map((item) => ({
      ...item,
      value: item.key,
      label: options?.dictTypeMap?.[item.key] || item.val,
    }))
  }
  return res
}

/**
 * 在「仅启用项」的选项列表中补回当前已选的停用项，避免表单回显成原始 id。
 * 补回的项带 __dictDisabled 标记，下拉中应置灰不可选。
 * @param enabledList 已过滤停用项的列表
 * @param rawList 含停用项的原始列表
 * @param selected 当前选中的值
 * @param valueKey 选项值字段，默认 id
 */
export function withSelectedDictOption(
  enabledList: DictionaryData,
  rawList: DictionaryData,
  selected?: any,
  valueKey = 'id',
): DictionaryData {
  if (selected === undefined || selected === null || selected === '') return enabledList
  if (enabledList.some((item) => String(item?.[valueKey]) === String(selected))) return enabledList
  const hit = rawList.find((item) => String(item?.[valueKey]) === String(selected))
  return hit ? [...enabledList, { ...hit, __dictDisabled: true }] : enabledList
}

/**
 * 按字典 key 取下拉选项：仅启用项 + 当前已选的停用项（置灰）
 */
export function getDictOptions(key: string, selected?: any, valueKey = 'id'): DictionaryData {
  return withSelectedDictOption(dictionariesKV.value[key] || [], dictionariesRawKV.value[key] || [], selected, valueKey)
}

/**
 * 重置所有字典缓存（清空已加载的数据）
 */
export function resetDictionaries(): void {
  dictionariesKV.value = {}
  dictionariesRawKV.value = {}
  pendingRequests.clear()
}

// ==================== 可选：兼容旧版的 initData（按需加载所有字典） ====================
/**
 * 初始化所有字典和字库（一次性加载全部，仅在需要预加载时调用）
 */
export async function initAllDictionaries(): Promise<void> {
  console.log('initAllDictionaries')
  const token = getToken()
  if (!token) return

  // 并行加载所有字典
  const loadPromises = dictConfigs.map((config) =>
    loadDictionary(config.key).catch((err) => console.error(`加载字典 ${config.key} 失败:`, err)),
  )
  await Promise.all(loadPromises)

  // 加载字库列表
  await loadTermBank().catch((err) => console.error('加载字库列表失败:', err))
}

// ==================== Vue Composable（提供给组件使用） ====================
/**
 * 字典管理 Hook
 * 返回响应式字典数据、字库列表以及各个字典的懒加载方法
 */
export function useDictionaries() {
  // 为每个字典动态生成加载方法（方便外部直接调用）
  const loadAcupunctureMethod = () => loadDictionary('acupunctureMethod')
  const loadAcupuncturePosition = () => loadDictionary('acupuncturePosition')
  const loadMedicantFunc = () => loadDictionary('medicantFunc')
  const loadMedicantBrand = () => loadDictionary('medicantBrand')
  const loadRatioMedicantStandard = () => loadDictionary('ratioMedicantStandard')
  const loadPayMethod = () => loadDictionary('payMethod')
  const loadCountryCodeEnum = () => loadDictionary('countryCodeEnum')
  const loadUnitList = () => loadDictionary('unitList')
  const loadLabExamType = () => loadDictionary('labExamType')

  return {
    // 响应式数据
    dictionariesKV: dictionariesKV as Ref<Record<string, DictionaryData>>,
    dictionariesRawKV: dictionariesRawKV as Ref<Record<string, DictionaryData>>,
    termBankList: termBankList as Ref<DictionaryData>,
    dictTypeList: dictTypeList as Ref<EnumItem[]>,
    unitTcEnMap: unitTcEnMap,

    // 通用懒加载方法（按 key 加载）
    loadDictionary,

    // 字库列表懒加载方法
    loadTermBank,

    // 枚举数据懒加载方法（type: 1-国籍、2-字典类型、3-证件类型）
    loadEnum,

    // 各字典独立的懒加载方法（推荐使用，更语义化）
    loadAcupunctureMethod,
    loadAcupuncturePosition,
    loadMedicantFunc,
    loadMedicantBrand,
    loadRatioMedicantStandard,
    loadPayMethod,
    loadCountryCodeEnum,
    loadUnitList,
    loadLabExamType,

    // 重置所有缓存
    resetDictionaries,
    // 兼容旧版的一次性全量加载
    initData: initAllDictionaries,
  }
}
